// src/components/Navbar.jsx
import React from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { NavLink, useLocation } from "react-router-dom";
import SearchBar from "./SearchBar"; // 🔍 buscador

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen((p) => !p);
  };

  // 🔑 Cierra el menú móvil automáticamente cuando cambia la ruta
  React.useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const navItems = [
    { label: "Inicio", to: "/" },
    { label: "Contáctanos", to: "/contacto" },
  ];

  const drawerTitleId = "mobile-menu-title";

  // Drawer para mobile, con SearchBar arriba
  const drawer = (
    <Box
      sx={{
        textAlign: "center",
        minWidth: "260px",
        p: 2,
      }}
      role="presentation"
    >
      {/* Título oculto para accesibilidad */}
      <Typography
        id={drawerTitleId}
        component="h2"
        sx={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(1px, 1px, 1px, 1px)",
          whiteSpace: "nowrap",
        }}
      >
        Menú de navegación
      </Typography>

      {/* 🔍 SearchBar dentro del menú móvil */}
      <Box sx={{ mb: 2 }}>
        <SearchBar />
        {/* Si quieres fondo más sólido en mobile:
           <SearchBar bgColor="rgba(255,255,255,0.95)" />
        */}
      </Box>

      {/* Menú mobile */}
      <nav aria-label="Navegación principal (móvil)">
        <List>
          {navItems.map((item) => (
            <ListItem
              key={item.label}
              component={NavLink}
              to={item.to}
              end
              onClick={handleDrawerToggle} // cierra el menú al elegir opción
              style={({ isActive }) => ({
                textDecoration: "none",
                background: isActive ? "rgba(0,0,0,0.04)" : "transparent",
              })}
              sx={{ textAlign: "left", borderRadius: 1 }}
            >
              <ListItemText
                primary={item.label.toUpperCase()}
                primaryTypographyProps={{
                  fontWeight: 600,
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "0.95rem",
                  color: "#000",
                }}
              />
            </ListItem>
          ))}
        </List>
      </nav>
    </Box>
  );

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: "white",
          color: "black",
          boxShadow: "none",
          borderBottom: "1px solid #e0e0e0",
          boxSizing: "border-box", // evita overflow horizontal
        }}
        role="banner"
      >
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            maxWidth: "1400px",
            mx: "auto",
            gap: 2,
            px: { xs: 2, sm: 3 }, // padding seguro dentro del toolbar
          }}
        >
          {/* Logo (izquierda) */}
          <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
            <NavLink
              to="/"
              aria-label="Ir al inicio - Nodo XXI"
              style={{ display: "inline-flex" }}
            >
              <img
                src="/img/logoNodo_COLOR.png"
                alt="Nodo XXI — Datos para la democracia"
                style={{ height: "80px", objectFit: "contain" }}
              />
            </NavLink>
          </Box>

          {/* Empuja resto del contenido a la derecha */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Zona derecha: menú desktop + searchbar + hamburguesa */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              ml: "auto",
            }}
          >
            {/* Menú desktop (solo sm+) */}
            <Box
              component="nav"
              aria-label="Navegación principal"
              sx={{
                display: { xs: "none", sm: "flex" },
                gap: 3,
                alignItems: "center",
              }}
            >
              {navItems.map((item) => (
                <Button
                  key={item.label}
                  component={NavLink}
                  to={item.to}
                  end
                  sx={{
                    color: "black",
                    fontWeight: 600,
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "0.875rem",
                    "&.active": {
                      textDecoration: "underline",
                      textUnderlineOffset: "4px",
                    },
                  }}
                >
                  {item.label.toUpperCase()}
                </Button>
              ))}
            </Box>

            {/* 🔍 SearchBar en navbar (solo md+) */}
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                width: 260,
              }}
            >
              <SearchBar />
            </Box>

            {/* Menú mobile (hamburguesa) */}
            <IconButton
              color="inherit"
              aria-label="Abrir menú de navegación"
              aria-haspopup="menu"
              aria-controls="mobile-menu"
              aria-expanded={mobileOpen ? "true" : "false"}
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ display: { xs: "block", sm: "none" } }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>

        {/* Drawer mobile */}
        <Drawer
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          sx={{ display: { xs: "block", sm: "none" } }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={drawerTitleId}
          PaperProps={{
            id: "mobile-menu",
            component: "div",
            role: "document",
          }}
        >
          {drawer}
        </Drawer>
      </AppBar>
    </>
  );
};

export default Navbar;
